import { type ReactNode, useEffect } from 'react';
import { NavLink, Outlet } from 'react-router';
import { Button } from '@launchpad-ui/components';

import { redirectToLogin, signOut } from '../../lib/auth';
import { useCurrentUser } from '../../lib/hooks/useAuth';

import styles from './Layout.module.css';

type NavItem = { to: string; label: string };

const NAV_ITEMS: ReadonlyArray<NavItem> = [
  { to: '/', label: 'Dashboard' },
  { to: '/tickets', label: 'Lookup' },
  { to: '/queue', label: 'Queue' },
  { to: '/review', label: 'Review Form' },
  { to: '/reviews', label: 'Reviews' },
  { to: '/new-hires', label: 'New Hires' },
  { to: '/reviewers', label: 'Reviewers' },
  { to: '/spotchecks', label: 'Spotchecks' },
  { to: '/feedback', label: 'Feedback' },
];

export function Layout() {
  const { data: user, isLoading, error } = useCurrentUser();
  const shouldRedirect = !isLoading && !user;

  useEffect(() => {
    if (shouldRedirect) {
      redirectToLogin();
    }
  }, [shouldRedirect]);

  if (isLoading || shouldRedirect) {
    return <div className={styles.loading}>Loading…</div>;
  }
  if (error || !user) {
    return <div className={styles.error}>Sign in required.</div>;
  }

  return (
    <div className={styles.shell}>
      <nav className={styles.nav}>
        <div className={styles.left}>
          <span className={styles.brand}>Ticket Review</span>
          <div className={styles.links}>
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) => (isActive ? `${styles.link} ${styles.linkActive}` : styles.link)}
              >
                {item.label}
              </NavLink>
            ))}
          </div>
        </div>
        <div className={styles.right}>
          <span className={styles.user}>{user.name || user.email}</span>
          <Button variant="minimal" onPress={signOut}>
            Sign out
          </Button>
        </div>
      </nav>
      <main className={styles.main}>
        <Outlet />
      </main>
    </div>
  );
}

export function PageShell({ children }: { children: ReactNode }) {
  return <div style={{ maxWidth: '80rem', margin: '0 auto' }}>{children}</div>;
}
